import React, { useState, useRef, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import "./InlineEditText.css";

/**
 * Edición inline estilo X-editable (Simple Text Field). Muestra el valor como
 * texto con subrayado punteado; al hacer clic se convierte en un input con
 * botones de guardar (✓) y cancelar (✕). Enter guarda, Esc cancela.
 *
 * onSave(value) debe devolver una promesa: si rechaza, se conserva la edición y
 * se muestra el error (leyendo field_errors.name / detail del backend).
 */
function InlineEditText({
  value,
  onSave,
  emptyText = "Sin definir",
  maxLength = 255,
  disabled = false,
  ariaLabel = "Editar",
  inputWidth = 220,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = () => {
    if (disabled) return;
    setDraft(value ?? "");
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
    setSaving(false);
  };

  const save = async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setError("No puede estar vacío.");
      return;
    }
    if (trimmed === (value ?? "")) {
      cancel();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(trimmed);
      setEditing(false);
    } catch (err) {
      const data = err?.response?.data;
      setError(
        data?.field_errors?.name?.[0] ??
          data?.name?.[0] ??
          data?.detail ??
          "No se pudo guardar."
      );
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      save();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        className="inline-edit-display"
        onClick={startEdit}
        disabled={disabled}
        aria-label={ariaLabel}
      >
        <span className={value ? "inline-edit-value" : "text-muted"}>
          {value || emptyText}
        </span>
        {!disabled && (
          <i className="mdi mdi-pencil-outline ms-2 small text-muted" aria-hidden="true" />
        )}
      </button>
    );
  }

  return (
    <div className="text-end">
      <div className="d-flex align-items-center gap-1 justify-content-end">
        <input
          ref={inputRef}
          type="text"
          className="form-control form-control-sm"
          style={{ maxWidth: inputWidth }}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          maxLength={maxLength}
          disabled={saving}
        />
        <button
          type="button"
          className="btn btn-sm btn-success d-inline-flex align-items-center"
          onClick={save}
          disabled={saving}
          aria-label="Guardar"
        >
          {saving ? (
            <Spinner size="sm" animation="border" />
          ) : (
            <i className="mdi mdi-check" aria-hidden="true" />
          )}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-light border d-inline-flex align-items-center"
          onClick={cancel}
          disabled={saving}
          aria-label="Cancelar"
        >
          <i className="mdi mdi-close" aria-hidden="true" />
        </button>
      </div>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  );
}

export { InlineEditText };
